import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertCircle, EyeOff } from 'lucide-react'

import { api, mensajeDeError } from '@/lib/api'
import { fechaHora, plataCentavos } from '@/lib/formato'
import { Ulid } from '@/lib/ulid'
import { Modal } from '@/componentes/ui/Modal'
import { LookupSelect, type ItemLookup } from '@/componentes/ui/LookupSelect'
import type { ArqueoTurno } from './tipos'

interface Props {
  /** Turno CERRADO al que se le agrega la nota. */
  turnoId: number

  /** Lo que se declaró al cerrar, para mostrarlo al lado del campo. */
  declaradoCentavos?: number | null

  /** Null cuando el que mira no puede ver el esperado. */
  esperadoCentavos?: number | null

  onCerrar: () => void
  onListo?: (arqueo: ArqueoTurno) => void
}

// Corregir o explicar el arqueo de un turno YA CERRADO.
//
// Existe porque un turno cerrado era un callejón sin salida: el cajero que
// tipeó 2.000 donde iba 20.000, o que después encuentra la plata en el otro
// cajón, no tenía NADA que pudiera hacer. El movimiento de caja no sirve
// —exige turno abierto y pide un permiso que el cajero no tiene—, así que la
// diferencia se quedaba sin nombre y el único que sabía qué había pasado era
// justo el que no podía escribir.
//
// Tres decisiones que se ven en esta pantalla:
//
// - NO pide el PIN de un encargado. Pedirle a otro que autorice una
//   EXPLICACIÓN es garantizar que nadie la escriba, y el permiso que hace
//   falta es el de cerrar el turno: quien arquea puede explicar su arqueo.
// - El recuento es OPCIONAL y el texto es OBLIGATORIO, no al revés. La mayoría
//   de las notas no corrigen el número: dicen por qué falta. Un número nuevo
//   sin explicación no sirve de nada, y una explicación sin número sí.
// - Lo anterior NO se pisa. La nota se suma a la cadena, así que «declaró
//   2.000 y corrigió a 20.000 diez minutos después» queda escrito con quién y
//   cuándo. Eso es lo que hace que una corrección legítima se lea legítima.
export function DialogoCorregirArqueo({
  turnoId,
  declaradoCentavos,
  esperadoCentavos,
  onCerrar,
  onListo,
}: Props) {
  const [pesos, setPesos] = useState('')
  const [motivo, setMotivo] = useState<ItemLookup | null>(null)
  const [detalle, setDetalle] = useState('')
  const [error, setError] = useState<string | null>(null)

  // El ULID se genera UNA vez, al abrir el diálogo, y no al mandar: es la
  // clave de idempotencia, y generarla al mandar la vuelve decorativa justo en
  // el caso para el que existe. Sin esto, el doble click dejaría DOS recuentos
  // y dos recuentos se leen como que alguien contó dos veces. La flecha va
  // explícita porque `useState(Ulid.nuevo)` lo rechaza el linter.
  const [ulid] = useState(() => Ulid.nuevo())

  const contado = pesos === '' ? null : Number(pesos) * 100

  const guardar = useMutation({
    mutationFn: () =>
      api.post<ArqueoTurno>(`/turnos/${turnoId}/nota`, {
        declaradoCentavos: contado,
        motivoId: motivo?.id ?? undefined,
        detalle: detalle.trim(),
        ulid,
      }),

    onSuccess: (r) => {
      onListo?.(r)
      onCerrar()
    },

    onError: (e) => setError(mensajeDeError(e, 'No se pudo anotar la corrección.')),
  })

  // La diferencia que daría el recuento nuevo, para que el que corrige vea el
  // número ANTES de guardar. Se calcula acá y no se manda: el servidor la
  // recalcula contra el esperado que se firmó, y ese es el que vale.
  const difNueva =
    contado != null && esperadoCentavos != null ? contado - esperadoCentavos : null

  return (
    <Modal
      onCerrar={onCerrar}
      titulo="Corregir el arqueo"
      descripcion="Se suma al turno con tu nombre. Lo que se declaró al cerrar no se borra."
      ancho="sm"
      pie={
        <div className="flex gap-2">
          <button type="button" onClick={onCerrar} className="btn-secundario flex-1">
            Cancelar
          </button>

          <button
            type="button"
            onClick={() => guardar.mutate()}
            disabled={guardar.isPending || detalle.trim() === ''}
            className="btn-primario flex-1"
          >
            {guardar.isPending ? 'Anotando…' : 'Anotar'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="contado"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Efectivo contado de nuevo{' '}
            <span className="font-normal text-tinta-tenue">(opcional)</span>
          </label>

          <input
            id="contado"
            inputMode="numeric"
            value={pesos}
            onChange={(e) => {
              setPesos(e.target.value.replace(/[^\d]/g, ''))
              setError(null)
            }}
            placeholder="Dejalo vacío si solo querés explicar la diferencia"
            className="num w-full rounded-lg border border-borde bg-papel px-3 py-3 text-right
                       text-[22px] outline-none focus:border-verde"
          />

          {declaradoCentavos != null && (
            <p className="mt-1.5 text-[12.5px] text-tinta-tenue">
              Al cerrar se declararon{' '}
              <span className="num">{plataCentavos(declaradoCentavos)}</span>.
            </p>
          )}

          {difNueva != null && (
            <p className="mt-1 text-[12.5px] text-tinta-suave">
              Con ese número{' '}
              {difNueva === 0 ? (
                <span className="font-semibold text-verde-oscuro">cerraría justo</span>
              ) : (
                <>
                  {difNueva > 0 ? 'sobraría' : 'faltaría'}{' '}
                  <span className="num font-semibold text-rojo">
                    {plataCentavos(Math.abs(difNueva))}
                  </span>
                </>
              )}
              .
            </p>
          )}

          {/* Cuando el arqueo sigue a ciegas no se puede adelantar la
              diferencia, y se dice POR QUÉ en lugar de dejar el hueco: si no,
              parece que la pantalla no calcula. */}
          {esperadoCentavos == null && (
            <p className="mt-1 flex items-start gap-1.5 text-[12.5px] text-tinta-tenue">
              <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              No se muestra cuánto tendría que haber, así que tampoco se puede adelantar la
              diferencia.
            </p>
          )}
        </div>

        <LookupSelect
          fuente="catalogo/MOTIVO_DIFERENCIA"
          etiqueta="Por qué no cerró"
          valor={motivo?.id ?? null}
          onCambio={(_, item) => {
            setMotivo(item)
            setError(null)
          }}
          placeholder="Le di mal el vuelto, tipeé mal el número…"
        />

        <div>
          <label
            htmlFor="detalle"
            className="mb-1.5 block text-[13px] font-medium text-tinta-suave"
          >
            Qué pasó
          </label>

          <textarea
            id="detalle"
            rows={3}
            value={detalle}
            onChange={(e) => {
              setDetalle(e.target.value.slice(0, 300))
              setError(null)
            }}
            placeholder="Conté de nuevo y aparecieron dos billetes de 10.000 abajo de la bandeja"
            className="w-full resize-none rounded-lg border border-borde bg-papel px-3 py-2.5
                       text-[14px] outline-none focus:border-verde"
          />

          <p className="mt-1 text-[12.5px] text-tinta-tenue">
            Es lo que va a leer el que revise el arqueo mañana. Sin esto la corrección no dice
            nada.
          </p>
        </div>

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}

/**
 * La cadena de notas de un arqueo, como lista para leer de arriba a abajo.
 *
 * Es un componente y no markup pegado en cada pantalla porque lo muestran las
 * DOS (el turno del mostrador y el control de caja de Gestión), y §1.3 ya dejó
 * escrito lo que pasa cuando el mismo bloque está escrito cinco veces:
 * arreglarlo en una no lo arregla en las otras.
 */
export function CadenaDeNotas({
  notas,
  cerradoAt,
}: {
  notas: NonNullable<ArqueoTurno['notas']>
  cerradoAt?: string | null
}) {
  if (notas.length === 0) return null

  return (
    <ul className="divide-y divide-borde/70">
      {notas.map((n) => (
        <li key={n.id} className="px-4 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[13.5px] font-medium text-tinta">
              {n.usuario}
              {n.motivo && <span className="font-normal text-tinta-tenue"> · {n.motivo}</span>}
            </span>

            <span className="text-[12.5px] text-tinta-tenue">
              {desdeElCierre(n.fechaAt, cerradoAt)}
            </span>
          </div>

          <p className="mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-tinta-suave">
            {n.detalle}
          </p>

          {n.declaradoCentavos != null && (
            <p className="mt-1.5 text-[13px] text-tinta-suave">
              Contó <span className="num font-semibold">{plataCentavos(n.declaradoCentavos)}</span>
              {n.diferenciaCentavos != null && (
                <>
                  {' · '}
                  {n.diferenciaCentavos === 0 ? (
                    <span className="text-verde-oscuro">cierra justo</span>
                  ) : (
                    <>
                      {n.diferenciaCentavos > 0 ? 'sobra' : 'falta'}{' '}
                      <span className="num text-rojo">
                        {plataCentavos(Math.abs(n.diferenciaCentavos))}
                      </span>
                    </>
                  )}
                </>
              )}
            </p>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Cuánto DESPUÉS del cierre se escribió la nota, en castellano.
 *
 * Es el dato que hace que una corrección legítima se lea distinto de una
 * sospechosa: «diez minutos después» y «a la mañana siguiente» no son lo
 * mismo, y el que audita no tiene que restar fechas a mano. Sin el cierre a
 * mano se cae a la fecha, que es la respuesta honesta.
 */
function desdeElCierre(fechaAt: string, cerradoAt?: string | null) {
  if (!cerradoAt) return fechaHora(fechaAt)

  const minutos = Math.round(
    (new Date(fechaAt).getTime() - new Date(cerradoAt).getTime()) / 60000,
  )

  if (!Number.isFinite(minutos) || minutos < 0) return fechaHora(fechaAt)
  if (minutos < 1) return 'al cerrar'
  if (minutos < 60) return `${minutos} min después de cerrar`

  const horas = Math.round(minutos / 60)

  if (horas < 24) return `${horas} h después de cerrar`

  return fechaHora(fechaAt)
}
